// @flow strict

import React from 'react';
import { graphql } from 'gatsby';
import { Grid, Box } from '@material-ui/core';

import { AboutUs } from '~components/about-us/about-us';
import { Layout } from '~components/layout/layout';
import { PageIntro } from '~components/layout/page-intro/page-intro';
import { SectionHeader } from '~components/layout/section-header/section-header';
import { FrontPost } from '~components/blog-elements/front-post';
import { HelpingHandsPreview } from '~components/blog-elements/helping-hands-preview';
import { NewsletterSignup } from '~components/newsletter-signup/newsletter-signup';
import { PostPreview } from '~components/blog-elements/post-preview';

import { useLanguageContext } from '~contexts/language-context';
import { createLinkPath } from '~utils/helpers/create-link-path';
import { withApp } from '../app';

import type { Alternates, PostNode } from '~types/post';
import type { Category } from '~types/category';

/**
 * Type of component properties.
 */
type BlogIndexProps = {
  data: {
    allMarkdownRemark: {
      nodes: Array<PostNode>,
    },
    allCategoriesJson: {
      nodes: Array<Category>,
    },
    site: {
      siteMetadata: { domain: string },
    },
  },

  pageContext: {
    alternates: Alternates,
    canonical: string,
  },
};

/**
 * Template of blog post index page
 *
 * @param {BlogIndexProps} props - component props
 *
 * @returns {Node}
 */
const BlogIndex = ({ data, pageContext }: BlogIndexProps) => {
  const posts = data.allMarkdownRemark.nodes;
  const categories = data.allCategoriesJson.nodes;
  const domain = data.site.siteMetadata.domain;

  const { language, loc } = useLanguageContext();

  const [frontPost, firstPreview, secondPreview, thirdPreview] = posts;

  const seoFields = {
    title: loc('_blog_title'),
    description: loc('_blog_description'),
    alternates: pageContext.alternates,
    canonical: createLinkPath({
      path: pageContext.canonical,
      domain,
      isAbsolute: true,
    }),
    meta: [
      {
        name: 'image',
        property: 'og:image',
        content: `${domain}/images/meta/blog/og${
          language === 'en' ? '_en' : ''
        }.png`,
      },
      {
        name: 'twitter:image',
        content: `${domain}/images/meta/blog/twitter${
          language === 'en' ? '_en' : ''
        }.png`,
      },
    ],
  };

  return (
    <Layout seoFields={seoFields} alternates={pageContext.alternates}>
      <section className="container">
        <PageIntro
          headline={loc('_blog_intro_headline')}
          text={loc('_blog_intro_text')}
        />
      </section>

      <div className="container">
        {/* Front post */}
        <FrontPost post={frontPost} />
      </div>

      <section className="container">
        {/* First 3 posts */}
        <Grid container spacing={4}>
          <Grid item md={4} sm={6}>
            <PostPreview
              post={firstPreview}
              hideImage={true}
              displayCategory={true}
            />
          </Grid>

          <Grid item md={4} sm={6}>
            <PostPreview post={secondPreview} displayCategory={true} />
          </Grid>

          <Box
            component={Grid}
            item
            md={4}
            display={{ xs: 'none', md: 'block' }}
          >
            <PostPreview
              post={thirdPreview}
              hideImage={true}
              displayCategory={true}
            />
          </Box>
        </Grid>
      </section>

      {/* Posts by category */}
      {categories.map((category) => {
        // get first three posts for this category
        const [firstEntry, secondEntry, thirdEntry] = posts.filter(
          (p) => p.frontmatter.category === category.name,
        );

        // get preview component
        const PreviewComponent =
          category.name === 'Helping Hands' ? HelpingHandsPreview : PostPreview;

        return (
          <section className="container" key={category.name}>
            {/* Category headline & link */}
            <SectionHeader
              name={category.name}
              link={{
                title: loc('_blog_read_more'),
                href: createLinkPath({
                  path: `/blog${category.link}`,
                  language,
                }),
              }}
            />

            {/* Blog posts for category */}
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6} md={12 / category.previews}>
                <PreviewComponent post={firstEntry} />
              </Grid>

              <Grid item xs={12} sm={6} md={12 / category.previews}>
                <PreviewComponent post={secondEntry} hideImage={true} />
              </Grid>

              {/* Render optional 3rd posts, hidden on mobile */}
              {category.previews === 3 && (
                <Box
                  component={Grid}
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  display={{ xs: 'none', md: 'block' }}
                >
                  <PreviewComponent post={thirdEntry} />
                </Box>
              )}
            </Grid>
          </section>
        );
      })}

      <div className="container">
        <NewsletterSignup />
      </div>

      <div className="container">
        <AboutUs />
      </div>
    </Layout>
  );
};

export default withApp(BlogIndex);

// noinspection JSUnusedGlobalSymbols
export const pageQuery = graphql`
  query BlogIndex($language: String!, $dateFormat: String!) {
    allMarkdownRemark(
      sort: { fields: [frontmatter___date], order: DESC }
      filter: {
        frontmatter: {
          language: { eq: $language }
          category: {
            regex: "/(News|How To's|Helping Hands|Events|Professional Voices|Scary New World)/"
          }
        }
        fields: { draft: { eq: false } }
      }
    ) {
      nodes {
        ...FrontPost
        ...PostPortrait
        ...PostPreview
      }
    }

    allCategoriesJson(filter: { language: { eq: $language } }) {
      nodes {
        name
        language
        link
        previews
      }
    }

    site {
      siteMetadata {
        domain
      }
    }
  }
`;
